import { Injectable, inject, DestroyRef } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  BaseNavigationItem as NavigationItem,
  BreakPointsMaxInPx
} from '@ui/shared/model';
import { NavigationAlignment } from './navigation.model';

export interface NavigationParams {
  dark?: boolean;
  overlay?: boolean;
  alignment?: NavigationAlignment;
  narrow?: boolean;
  open?: boolean;
  navigationItems?: NavigationItem[];
}

@Injectable()
export class NavigationService {
  #destroyRef = inject(DestroyRef);
  private observer = inject(BreakpointObserver);

  private _dark = new BehaviorSubject<boolean>(false);
  private _overlay = new BehaviorSubject<boolean>(false);
  private _alignment = new BehaviorSubject<NavigationAlignment>(
    NavigationAlignment.LEFT
  );
  private _narrow = new BehaviorSubject<boolean>(false);
  private _narrowManualChange = new BehaviorSubject<boolean>(false);
  private _open = new BehaviorSubject<boolean>(false);
  private _openFloatingSubMenu = new BehaviorSubject<boolean>(null);
  private _mobileView = new BehaviorSubject<boolean>(null);
  private _floatingSubMenuParentItem = new BehaviorSubject<NavigationItem>(
    null
  );
  private _navigationItems = new BehaviorSubject<NavigationItem[]>([]);

  public initialised = false;

  public get dark$() {
    return this._dark.asObservable().pipe(distinctUntilChanged());
  }

  public get overlay$() {
    return this._overlay.asObservable().pipe(distinctUntilChanged());
  }

  public get alignment$() {
    return this._alignment.asObservable().pipe(distinctUntilChanged());
  }

  public get narrow$() {
    return this._narrow.asObservable().pipe(distinctUntilChanged());
  }

  public get narrowManualChange$() {
    return this._narrowManualChange.asObservable().pipe(distinctUntilChanged());
  }

  public get open$() {
    return this._open.asObservable().pipe(distinctUntilChanged());
  }

  public get mobileView$() {
    return this._mobileView.asObservable().pipe(distinctUntilChanged());
  }

  public get openInMobileView$() {
    return combineLatest(
      [this.open$, this.mobileView$],
      (open, mobileView) => open && mobileView
    );
  }

  public get openFloatingSubMenu$() {
    return this._openFloatingSubMenu
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  public get floatingSubMenuParentItem$() {
    return this._floatingSubMenuParentItem
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  public get navigationItems$() {
    return this._navigationItems.asObservable().pipe(distinctUntilChanged());
  }

  public init(params: NavigationParams = {}) {
    const {
      dark = false,
      overlay = false,
      alignment = NavigationAlignment.LEFT,
      open = false,
      narrow = false,
      navigationItems = []
    } = params;

    this._dark.next(dark);
    this._overlay.next(overlay);
    this._alignment.next(alignment);
    this._narrow.next(narrow);
    this._narrowManualChange.next(narrow);
    this._open.next(open);
    this._navigationItems.next(navigationItems);

    combineLatest([this.mobileView$, this.overlay$])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([mobileView, overlay]) => {
        if (!overlay) {
          if (mobileView) {
            this.close();
          } else {
            this.open();
          }
        }
      });

    this.observer
      .observe(`(max-width: ${BreakPointsMaxInPx.MD}px)`)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(result => {
        this.setMobileView(result.matches);
      });

    this.initialised = true;
  }

  public complete() {
    this._dark.complete();
    this._overlay.complete();
    this._alignment.complete();
    this._narrow.complete();
    this._narrowManualChange.complete();
    this._open.complete();
    this._mobileView.complete();
    this._openFloatingSubMenu.complete();
    this._floatingSubMenuParentItem.complete();
    this._navigationItems.complete();
  }

  public setDark(dark: boolean) {
    this._dark.next(dark);
  }

  public setOverlay(overlay: boolean) {
    this._overlay.next(overlay);
  }

  public setAlignment(alignment: NavigationAlignment) {
    this._alignment.next(alignment);
  }

  public setNarrow(narrow: boolean) {
    this._narrow.next(narrow);
    this._narrowManualChange.next(narrow);
  }

  public setMobileView(mobileView: boolean) {
    this._mobileView.next(mobileView);
  }

  public open() {
    this._open.next(true);
  }

  public close() {
    this._open.next(false);
    this._openFloatingSubMenu.next(false);
  }

  public openFloatingSubMenu() {
    this._openFloatingSubMenu.next(true);
  }

  public closeFloatingSubMenu() {
    this._openFloatingSubMenu.next(false);
  }

  public setFloatingSubMenuParentItem(item: NavigationItem) {
    this._floatingSubMenuParentItem.next(item);
  }

  public setNavigationItems(items: NavigationItem[]) {
    this._navigationItems.next(items);
  }

  public setNarrowManualChange(narrow: boolean) {
    this._narrowManualChange.next(narrow);
  }
}
